/**
 * MCP HTTP request handlers — routes /mcp requests to AgentVaultMcpServer.
 *
 * Supports both self-managed HTTP and OpenClaw managed routes.
 */
import type { IncomingMessage, ServerResponse } from "node:http";
import type { AgentVaultMcpServer } from "./mcp-server.js";

/**
 * Create an HTTP request handler for MCP routes.
 *
 * Routes:
 *   POST /mcp  — MCP Streamable HTTP messages
 *   GET  /mcp  — MCP SSE stream (for server-initiated messages)
 *   DELETE /mcp — Close MCP session
 *
 * All other paths are passed through to the next handler.
 */
export function createMcpHandler(
  mcpServer: AgentVaultMcpServer | null,
  basePath: string = "/mcp",
): (req: IncomingMessage, res: ServerResponse, next?: () => void) => void {
  return (req, res, next) => {
    const url = new URL(req.url ?? "/", `http://${req.headers.host ?? "localhost"}`);
    const pathname = url.pathname;

    // Only handle requests to the MCP path
    if (!pathname.startsWith(basePath)) {
      if (next) next();
      return;
    }

    if (!mcpServer) {
      res.writeHead(503, { "Content-Type": "application/json" });
      res.end(JSON.stringify({ error: "MCP server not initialized" }));
      return;
    }

    // Delegate to the MCP server
    mcpServer.handleRequest(req, res).catch((err: unknown) => {
      console.error("[MCP] Request handling error:", err);
      if (!res.headersSent) {
        res.writeHead(500, { "Content-Type": "application/json" });
        res.end(JSON.stringify({ error: "Internal MCP error" }));
      }
    });
  };
}

/**
 * Build a status object with MCP info for the /status endpoint.
 */
export function getMcpStatus(
  mcpServer: AgentVaultMcpServer | null,
): { mcp_enabled: boolean; mcp_skills_count: number } {
  if (!mcpServer) {
    return { mcp_enabled: false, mcp_skills_count: 0 };
  }
  return {
    mcp_enabled: true,
    mcp_skills_count: mcpServer.skillCount,
  };
}
